// api/research.js - Research Management API

import { apiClient } from './client';

class ResearchAPI {
  constructor(client) {
    this.client = client;
  }

  // ========== BASIC CRUD ==========

  async getResearch(campaignId) {
    return this.client.request(`/api/research?campaign_id=${campaignId}`);
  }

  async getResearchItem(researchId) {
    return this.client.request(`/api/research/${researchId}`);
  }

  async createResearch(campaignId, researchType, researchName, progress = 0) {
    return this.client.request('/api/research', {
      method: 'POST',
      body: JSON.stringify({
        campaign_id: campaignId,
        research_type: researchType,
        research_name: researchName,
        progress,
      }),
    });
  }

  async updateResearch(researchId, updates) {
    return this.client.request(`/api/research/${researchId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteResearch(researchId) {
    return this.client.request(`/api/research/${researchId}`, {
      method: 'DELETE',
    });
  }

  // ========== PROGRESS MANAGEMENT ==========

  async updateResearchProgress(researchId, progress) {
    return this.updateResearch(researchId, { progress });
  }

  async addResearchProgress(researchId, progressAmount) {
    const research = await this.getResearchItem(researchId);
    const newProgress = Math.min((research.progress || 0) + progressAmount, 100);
    
    // Auto-complete if reaches 100
    if (newProgress >= 100) {
      return this.completeResearch(researchId);
    }
    
    return this.updateResearchProgress(researchId, newProgress);
  }

  async completeResearch(researchId) {
    return this.updateResearch(researchId, {
      progress: 100,
      completed: 1,
      completed_at: new Date().toISOString(),
    });
  }

  // ========== QUERIES ==========

  async getActiveResearch(campaignId) {
    const research = await this.getResearch(campaignId);
    return research.filter(r => r.completed === 0);
  }

  async getCompletedResearch(campaignId) {
    const research = await this.getResearch(campaignId);
    return research.filter(r => r.completed === 1);
  }

  async getResearchByType(campaignId, researchType) {
    const research = await this.getResearch(campaignId);
    return research.filter(r => r.research_type === researchType);
  }

  async getCurrentResearch(campaignId) {
    const activeResearch = await this.getActiveResearch(campaignId);
    // Return the one with highest progress (assumed to be "current")
    return activeResearch.sort((a, b) => (b.progress || 0) - (a.progress || 0))[0] || null;
  }
}

// Export singleton instance
export const researchAPI = new ResearchAPI(apiClient);

// Also export class
export default ResearchAPI;